import { CSSProperties, ReactNode } from 'react'
import {
  HIGHLIGHT_COLOR,
  useFieldHighlight,
  WALLET_FIELD_LABELS,
} from './highlight'
import { FieldHighlightProps, WalletField } from './types'

interface Props extends FieldHighlightProps {
  field: WalletField
  children: ReactNode
  /** Corner radius of the outline ring, to hug the wrapped element. */
  radius?: number
  /**
   * Merged onto the wrapper so a zone can *be* the element's existing
   * container (carry its flex/padding) instead of adding a nesting level that
   * would shift the faithful layout.
   */
  style?: CSSProperties
}

/**
 * Wraps one region of a pass face and ties it to a form control: it lights up
 * (outline ring + a floating tag naming the field) whenever that field is
 * active, and marks the field active while the pointer is over it. Meant to
 * replace an element's existing wrapper `div` — pass the same `style` — so the
 * preview stays pixel-faithful.
 */
const WalletFieldZone = ({
  field,
  activeField,
  onActiveFieldChange,
  radius = 8,
  style,
  children,
}: Props) => {
  const { isActive, handlers } = useFieldHighlight(field, {
    activeField,
    onActiveFieldChange,
  })

  return (
    <div
      {...handlers}
      style={{
        position: 'relative',
        borderRadius: radius,
        outline: `2px solid ${isActive ? HIGHLIGHT_COLOR : 'transparent'}`,
        outlineOffset: 2,
        transition: 'outline-color 120ms ease',
        ...style,
      }}
    >
      {isActive && (
        <span
          style={{
            position: 'absolute',
            top: -11,
            left: 8,
            zIndex: 5,
            background: HIGHLIGHT_COLOR,
            color: '#fff',
            fontSize: 10,
            fontWeight: 600,
            lineHeight: '16px',
            padding: '0 6px',
            borderRadius: 8,
            whiteSpace: 'nowrap',
            pointerEvents: 'none',
            boxShadow: '0 2px 6px rgba(0, 0, 0, 0.25)',
          }}
        >
          {WALLET_FIELD_LABELS[field]}
        </span>
      )}
      {children}
    </div>
  )
}

export default WalletFieldZone
